
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown, Menu } from "lucide-react";
import { Link, useLocation } from 'react-router-dom';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';

const Navbar = () => {
  const [homeownersOpen, setHomeownersOpen] = useState(false);
  const [aboutUsOpen, setAboutUsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;
  const isRRMPage = location.pathname === '/rrm';

  const closeMobile = () => {
    setMobileOpen(false);
    setHomeownersOpen(false);
    setAboutUsOpen(false);
  };

  return (
    <nav className={cn("fixed top-0 left-0 right-0 z-50 transition-all duration-300 w-full bg-white/20 backdrop-blur-md shadow-sm")}>
      <div className="w-full px-4 sm:px-6 lg:px-8 mx-auto">
        {/* Mobile top bar: hamburger | centered logo | Log In */}
        {!isRRMPage && (
          <div className="md:hidden flex items-center h-14">
            <div className="flex-1 flex justify-start">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Open menu"
                    className="h-10 w-10 text-gray-700 hover:bg-brand-blue/5"
                  >
                    <Menu className="h-6 w-6" aria-hidden="true" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[85%] max-w-sm p-0 flex flex-col">
                  <SheetTitle className="sr-only">Main navigation</SheetTitle>
                  <div className="flex items-center px-4 h-14 border-b">
                    <Link to="/" onClick={closeMobile} aria-label="House Beat Home">
                      <img
                        src="/housebeat_logo.svg"
                        alt="House Beat Logo"
                        className="h-8 w-auto"
                        width="92"
                        height="32"
                      />
                    </Link>
                  </div>
                  <div className="flex-1 overflow-y-auto px-2 py-3">
                    <Link
                      to="/"
                      onClick={closeMobile}
                      className={`block px-3 py-2.5 rounded-md text-sm font-bold transition-colors ${
                        isActive('/') ? 'text-brand-blue bg-brand-blue/5' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Home
                    </Link>

                    {/* Homeowner Benefits group */}
                    <button
                      type="button"
                      onClick={() => setHomeownersOpen(!homeownersOpen)}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-bold text-gray-700 hover:bg-gray-50"
                      aria-expanded={homeownersOpen}
                    >
                      Homeowner Benefits
                      <ChevronDown className={`h-4 w-4 transition-transform ${homeownersOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                    </button>
                    {homeownersOpen && (
                      <div className="ml-3 border-l border-gray-200 pl-3 mt-1 mb-2 space-y-1">
                        {[
                          { to: '/text-concierge', label: 'Text Concierge' },
                          { to: '/your-monthly-beat', label: 'Stay Organized' },
                          { to: '/home-improvement', label: 'Improvements Made Easy' },
                          { to: '/repairs', label: 'Help with Repairs' },
                          { to: '/maintenance', label: 'Track & Manage Maintenance' },
                          { to: '/home-report-roadmap', label: 'Roadmap For Your Home' },
                        ].map(item => (
                          <Link
                            key={item.to}
                            to={item.to}
                            onClick={closeMobile}
                            className={`block px-3 py-2 rounded-md text-sm transition-colors ${
                              isActive(item.to) ? 'text-brand-blue bg-brand-blue/5 font-medium' : 'text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}

                    {/* About Us group */}
                    <button
                      type="button"
                      onClick={() => setAboutUsOpen(!aboutUsOpen)}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-bold text-gray-700 hover:bg-gray-50"
                      aria-expanded={aboutUsOpen}
                    >
                      About Us
                      <ChevronDown className={`h-4 w-4 transition-transform ${aboutUsOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                    </button>
                    {aboutUsOpen && (
                      <div className="ml-3 border-l border-gray-200 pl-3 mt-1 mb-2 space-y-1">
                        {[
                          { to: '/meet-the-founders', label: 'Meet the Founders' },
                          { to: '/our-vision', label: 'Our Home Health Vision' },
                          { to: '/blog', label: 'The Beat Blog' },
                        ].map(item => (
                          <Link
                            key={item.to}
                            to={item.to}
                            onClick={closeMobile}
                            className={`block px-3 py-2 rounded-md text-sm transition-colors ${
                              isActive(item.to) ? 'text-brand-blue bg-brand-blue/5 font-medium' : 'text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="border-t p-4">
                    <a href="https://app.housebeat.com/welcome" target="_blank" rel="noopener noreferrer" onClick={closeMobile}>
                      <Button variant="brand" className="w-full font-bold">
                        Get Started
                      </Button>
                    </a>
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            <Link to="/" className="flex-shrink-0" aria-label="House Beat Home">
              <img
                src="/housebeat_logo.svg"
                alt="House Beat Logo - Home Management Platform"
                className="h-8 w-auto"
                width="92"
                height="32"
                loading="eager"
              />
            </Link>

            <div className="flex-1 flex justify-end">
              <a href="https://app.housebeat.com/signin" target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" size="sm" className="font-bold bg-white text-black border-0 hover:bg-gray-50">
                  Log In
                </Button>
              </a>
            </div>
          </div>
        )}

        {/* RRM page mobile: just the logo */}
        {isRRMPage && (
          <div className="md:hidden flex items-center justify-center h-14">
            <Link to="/" className="flex-shrink-0" aria-label="House Beat Home">
              <img
                src="/housebeat_logo.svg"
                alt="House Beat Logo"
                className="h-8 w-auto"
                width="92"
                height="32"
                loading="eager"
              />
            </Link>
          </div>
        )}

        {/* Desktop bar */}
        <div className="hidden md:flex items-center h-16">
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center" aria-label="House Beat Home">
              <img
                src="/housebeat_logo.svg"
                alt="House Beat Logo - Home Management Platform"
                className="h-12 w-auto"
                width="137"
                height="48"
                loading="eager"
              />
            </Link>
          </div>

          {!isRRMPage && (
            <div className="flex items-center space-x-4 ml-12">
              {location.pathname !== '/' && (
                <Link
                  to="/"
                  className="text-gray-700 hover:text-brand-blue font-bold px-3 py-2 rounded-md transition-colors"
                >
                  Home
                </Link>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center text-gray-700 hover:text-brand-blue font-bold px-3 py-2 rounded-md transition-colors">
                  Homeowner Benefits
                  <ChevronDown className="ml-1 h-4 w-4" aria-hidden="true" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64">
                  <DropdownMenuItem asChild>
                    <Link to="/text-concierge" className="flex flex-col items-start p-3 space-y-1 hover:bg-brand-blue/10 hover:text-brand-blue transition-colors">
                      <div className="font-medium">Text Concierge</div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/your-monthly-beat" className="flex flex-col items-start p-3 space-y-1 hover:bg-brand-blue/10 hover:text-brand-blue transition-colors">
                      <div className="font-medium">Stay Organized</div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/home-improvement" className="flex flex-col items-start p-3 space-y-1 hover:bg-brand-blue/10 hover:text-brand-blue transition-colors">
                      <div className="font-medium">Improvements Made Easy</div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/repairs" className="flex flex-col items-start p-3 space-y-1 hover:bg-brand-blue/10 hover:text-brand-blue transition-colors">
                      <div className="font-medium">Help with Repairs</div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/maintenance" className="flex flex-col items-start p-3 space-y-1 hover:bg-brand-blue/10 hover:text-brand-blue transition-colors">
                      <div className="font-medium">Track & Manage Maintenance</div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/home-report-roadmap" className="flex flex-col items-start p-3 space-y-1 hover:bg-brand-blue/10 hover:text-brand-blue transition-colors">
                      <div className="font-medium">Roadmap For Your Home</div>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center text-gray-700 hover:text-brand-blue font-bold px-3 py-2 rounded-md transition-colors">
                  About Us
                  <ChevronDown className="ml-1 h-4 w-4" aria-hidden="true" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64">
                  <DropdownMenuItem asChild>
                    <Link to="/meet-the-founders" className="flex flex-col items-start p-3 space-y-1 hover:bg-brand-blue/10 hover:text-brand-blue transition-colors">
                      <div className="font-medium">Meet the Founders</div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/our-vision" className="flex flex-col items-start p-3 space-y-1 hover:bg-brand-blue/10 hover:text-brand-blue transition-colors">
                      <div className="font-medium">Our Home Health Vision</div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/blog" className="flex flex-col items-start p-3 space-y-1 hover:bg-brand-blue/10 hover:text-brand-blue transition-colors">
                      <div className="font-medium">The Beat Blog</div>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {!isRRMPage && (
            <div className="flex items-center space-x-3 ml-auto">
              <a href="https://app.housebeat.com/welcome" target="_blank" rel="noopener noreferrer">
                <Button variant="brand" size="sm" className="font-bold">
                  Get Started
                </Button>
              </a>
              <a href="https://app.housebeat.com/signin" target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" size="sm" className="font-bold bg-white text-black border-0 hover:bg-gray-50">
                  Log In
                </Button>
              </a>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
